/**
 * Browser Tracking Hook
 * Manages browser fingerprint and session tracking
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  useCreateBrowserFingerprint,
  useCreateBrowserSession,
  browserSessionApi,
  CreateBrowserFingerprintRequest,
} from "../api-data/iam/browser-tracking";
import {
  generateBrowserFingerprintKey,
  collectBrowserInfo,
  generateSessionId,
} from "../utils/browserInfo";
import { getCookie, setCookie } from "../utils/cookies";

const FINGERPRINT_KEY_COOKIE = "browserFingerprintKey";
const FINGERPRINT_ID_COOKIE = "browserFingerprintId";
const SESSION_ID_STORAGE = "browserSessionId";
export const SESSION_DB_ID_STORAGE = "browserSessionDbId";

interface UseBrowserTrackingReturn {
  isInitialized: boolean;
  //   fingerprintId: string | null;
  sessionId: string | null;
  sessionDbId: number | null;
  error: Error | null;
  initializeFingerprint: () => Promise<void>;
  createSession: () => Promise<void>;
  addUserToSession: (userId: number) => Promise<void>;
  clearSession: () => void;
}

export function useBrowserTracking(): UseBrowserTrackingReturn {
  const [isInitialized, setIsInitialized] = useState(false);
  //   const [fingerprintId, setFingerprintId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionDbId, setSessionDbId] = useState<number | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // Use refs to track session creation state without causing re-renders
  const isCreatingSessionRef = useRef(false);
  const sessionCreationFailedRef = useRef(false);
  const addedUserIdsRef = useRef<Set<number>>(new Set());

  const createFingerprintMutation = useCreateBrowserFingerprint();
  const createSessionMutation = useCreateBrowserSession();

  /**
   * Create a new browser session (without any logged-in users initially)
   */
  const createSession = useCallback(async () => {
    // Prevent duplicate session creation attempts
    // if (isCreatingSessionRef.current || sessionCreationFailedRef.current) {
    //   console.log(
    //     "Skipping session creation - already in progress or previously failed"
    //   );
    //   return;
    // }

    try {
      // Check if session already exists
      const existingSessionId = sessionStorage.getItem(SESSION_ID_STORAGE);
      if (existingSessionId) {
        console.log("Session already exists:", existingSessionId);
        return;
      }

      const fingerprintKey = getCookie(FINGERPRINT_KEY_COOKIE);
      if (!fingerprintKey) {
        throw new Error("Browser fingerprint not initialized");
      }

      isCreatingSessionRef.current = true;
      const newSessionId = generateSessionId();

      // Collect device information
      const browserInfo = collectBrowserInfo();

      const sessionData = {
        sessionId: newSessionId,
        browserFingerprintKey: parseInt(fingerprintKey, 10),
        loginUserIds: [],
        deviceManufacturer: browserInfo.deviceManufacturer,
        deviceModel: browserInfo.deviceModel,
      };

      await createSessionMutation.mutateAsync(sessionData);

      // Store in sessionStorage (cleared when browser closes)
      sessionStorage.setItem(SESSION_ID_STORAGE, newSessionId);
      //   sessionStorage.setItem(SESSION_DB_ID_STORAGE, response.id!.toString());

      setSessionId(newSessionId);
      //   setSessionDbId(response.id!);
      sessionCreationFailedRef.current = false;
    } catch (err) {
      console.error("Failed to create browser session. Cleaning up...", err);
      setError(err as Error);
      sessionCreationFailedRef.current = true;

      // Cleanup on failure
      sessionStorage.removeItem(SESSION_ID_STORAGE);
      sessionStorage.removeItem(SESSION_DB_ID_STORAGE);
      setSessionId(null);
      //   setSessionDbId(null);

      throw err;
    } finally {
      isCreatingSessionRef.current = false;
    }
  }, [createSessionMutation]);

  // Load existing fingerprint and session from storage
  useEffect(() => {
    const storedFingerprintId = getCookie(FINGERPRINT_ID_COOKIE);
    const storedSessionId = sessionStorage.getItem(SESSION_ID_STORAGE);
    const storedSessionDbId = sessionStorage.getItem(SESSION_DB_ID_STORAGE);

    if (storedFingerprintId) {
      //   setFingerprintId(storedFingerprintId);
      setIsInitialized(true);

      // If fingerprint exists but session is cleared (e.g., browser restart), create new session
      if (
        !storedSessionId &&
        !isCreatingSessionRef.current &&
        !sessionCreationFailedRef.current
      ) {
        createSession().catch((err) => {
          console.error("Failed to create session on initialization:", err);
        });
      }
    }

    if (storedSessionId) {
      setSessionId(storedSessionId);
    }

    if (storedSessionDbId) {
      setSessionDbId(parseInt(storedSessionDbId, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Initialize browser fingerprint
   * Creates a new fingerprint if one doesn't exist
   */
  const initializeFingerprint = useCallback(async () => {
    try {
      // Check if already initialized
      const existingFingerprintKey = getCookie(FINGERPRINT_KEY_COOKIE);

      if (existingFingerprintKey) {
        console.log(
          "Fingerprint already exists in cookies:",
          existingFingerprintKey
        );
        // setFingerprintId(existingFingerprintId);
        setIsInitialized(true);

        // Check if session exists, if not create one
        const existingSessionId = sessionStorage.getItem(SESSION_ID_STORAGE);
        if (!existingSessionId) {
          console.log(
            "No session found, creating session for existing fingerprint..."
          );
          sessionCreationFailedRef.current = false;
          try {
            await createSession();
            console.log(
              "Session created successfully for existing fingerprint"
            );
          } catch (sessionErr) {
            console.error(
              "Failed to create session for existing fingerprint:",
              sessionErr
            );
          }
        } else {
          console.log("Session already exists:", existingSessionId);
        }
        return;
      }

      console.log("Generating new browser fingerprint...");
      // Generate fingerprint key
      const fingerprintKey = generateBrowserFingerprintKey();
      const browserInfo = collectBrowserInfo();

      const fingerprintData: CreateBrowserFingerprintRequest = {
        browserFingerprintKey: fingerprintKey,
        ...browserInfo,
      };

      console.log("Creating fingerprint on backend...", fingerprintData);
      // Create fingerprint on backend
      await createFingerprintMutation.mutateAsync(fingerprintData);

      //   console.log("Fingerprint created successfully:", response);
      // Store in cookies for persistence across sessions (365 days)
      //   setCookie(FINGERPRINT_ID_COOKIE, response.id!.toString(), { days: 365 });
      setCookie(FINGERPRINT_KEY_COOKIE, fingerprintKey.toString(), {
        days: 365,
      });

      console.log("Fingerprint cookies set:", {
        // id: response.id!.toString(),
        key: fingerprintKey.toString(),
      });

      //   setFingerprintId(response.id!.toString());
      setIsInitialized(true);

      // Reset session creation failed flag when fingerprint is successfully created
      sessionCreationFailedRef.current = false;

      // Create session immediately after fingerprint initialization
      // Use separate try-catch to ensure fingerprint cookies persist even if session creation fails
      console.log("Attempting to create session...");
      try {
        await createSession();
        console.log("Session created successfully");
      } catch (sessionErr) {
        console.error(
          "Failed to create session after fingerprint initialization:",
          sessionErr
        );
        // Fingerprint is already saved in cookies, session can be retried later
      }
    } catch (err) {
      console.error("Failed to initialize browser fingerprint:", err);
      setError(err as Error);

      // Even if fingerprint creation failed, check if we have fingerprint in cookies
      const existingFingerprintKey = getCookie(FINGERPRINT_KEY_COOKIE);
      if (existingFingerprintKey) {
        console.log(
          "Fingerprint exists in cookies despite error, checking for session..."
        );
        setIsInitialized(true);

        // Check if session exists, if not create one
        const existingSessionId = sessionStorage.getItem(SESSION_ID_STORAGE);
        if (!existingSessionId) {
          console.log("No session found, attempting to create session...");
          sessionCreationFailedRef.current = false;
          try {
            await createSession();
            console.log("Session created successfully after error recovery");
          } catch (sessionErr) {
            console.error(
              "Failed to create session after error recovery:",
              sessionErr
            );
          }
        }
      }
    }
  }, [createFingerprintMutation, createSession]);

  /**
   * Add a user to the current session
   */
  const addUserToSession = useCallback(async (userId: number) => {
    try {
      // Check if user was already added to prevent duplicate calls
      if (addedUserIdsRef.current.has(userId)) {
        console.log("User already added to session:", userId);
        return;
      }

      const currentSessionDbId = sessionStorage.getItem(SESSION_DB_ID_STORAGE);
      if (!currentSessionDbId) {
        console.warn("No active session to add user to");
        return;
      }

      const dbId = parseInt(currentSessionDbId, 10);

      console.log("Fetching current session to add user:", userId);
      // Get current session
      const currentSession = await browserSessionApi.get(dbId);

      // Add new user to loginUserIds (using Set to avoid duplicates)
      const updatedUserIds = Array.from(
        new Set([...(currentSession.loginUserIds || []), userId])
      );

      // Check if user is already in the list
      if (currentSession.loginUserIds?.includes(userId)) {
        console.log("User already exists in session loginUserIds:", userId);
        addedUserIdsRef.current.add(userId);
        return;
      }

      console.log("Updating session with new user:", userId);
      // Update session
      const fingerprintKey = getCookie(FINGERPRINT_KEY_COOKIE);
      await browserSessionApi.update(dbId, {
        sessionId: currentSession.sessionId,
        browserFingerprintKey: parseInt(fingerprintKey!, 10),
        loginUserIds: updatedUserIds,
        deviceManufacturer: currentSession.deviceManufacturer,
        deviceModel: currentSession.deviceModel,
      });

      // Mark user as added
      addedUserIdsRef.current.add(userId);
      console.log("User successfully added to session:", userId);
    } catch (err) {
      console.error("Failed to add user to session:", err);
      setError(err as Error);
    }
  }, []);

  /**
   * Clear the current session (useful for logout)
   */
  const clearSession = useCallback(() => {
    sessionStorage.removeItem(SESSION_ID_STORAGE);
    sessionStorage.removeItem(SESSION_DB_ID_STORAGE);
    setSessionId(null);
    setSessionDbId(null);
  }, []);

  return {
    isInitialized,
    // fingerprintId,
    sessionId,
    sessionDbId,
    error,
    initializeFingerprint,
    createSession,
    addUserToSession,
    clearSession,
  };
}

/**
 * Helper hook to ensure browser tracking is initialized
 */
export function useEnsureBrowserTracking() {
  const tracking = useBrowserTracking();

  useEffect(() => {
    if (!tracking.isInitialized) {
      tracking.initializeFingerprint();
    }
  }, [tracking]);

  return tracking;
}
